import { useEffect, useRef, useCallback, useState } from "react";
import { useAuth } from "@clerk/react";
import { Redirect } from "wouter";
import { Shield, Lock, WifiOff } from "lucide-react";
import { clearRheumHubLocalStorage } from "@/lib/local-storage-keys";

const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;

const ACTIVITY_EVENTS: (keyof DocumentEventMap)[] = [
  "mousemove",
  "keydown",
  "mousedown",
  "touchstart",
  "scroll",
  "click",
];

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

type GateState =
  | "loading"
  | "verified"
  | "unauthenticated"
  | "forbidden"
  | "network-error"
  | "timed-out";

/**
 * AdminGate wraps all /admin routes with four layers of protection:
 *   1. Clerk authentication — redirects to sign-in if not signed in
 *   2. Admin API check — calls GET /api/admin/me; renders 403 if signed in
 *      but not an admin (rather than silently redirecting)
 *   3. MFA enforcement — the API returns 403 if MFA has not been completed
 *   4. 15-minute inactivity timeout — signs the user out and shows timeout
 *      screen on idle
 *
 * States:
 *   - unauthenticated  → redirect to /sign-in
 *   - forbidden        → non-2xx API response (true 403 / 401 — not admin / MFA missing)
 *   - network-error    → fetch failed (offline, server down) — shows retry prompt
 *   - timed-out        → inactivity timer fired and user was signed out
 */
export default function AdminGate({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded, signOut } = useAuth();
  const [state, setState] = useState<GateState>("loading");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Dev-only E2E bypass: allows Playwright tests to render admin pages without
  // a real Clerk session. The backing API responses are stubbed by the test
  // via page.route(). Never active in production builds.
  const e2eBypass =
    import.meta.env.DEV &&
    typeof window !== "undefined" &&
    window.localStorage.getItem("__e2e_bypass_auth__") === "true";

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      setState("timed-out");
      try {
        clearRheumHubLocalStorage();
        await signOut();
      } catch {
        // best-effort
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, [signOut]);

  useEffect(() => {
    if (state !== "verified") return;

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) => document.addEventListener(event, resetTimer, { passive: true }));

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((event) => document.removeEventListener(event, resetTimer));
    };
  }, [state, resetTimer]);

  const checkAdminAccess = useCallback(() => {
    if (!e2eBypass && (!isLoaded || !isSignedIn)) return;
    setState("loading");
    let cancelled = false;

    fetch(`${basePath}/api/admin/me`, { credentials: "include" })
      .then((res) => {
        if (cancelled) return;
        if (res.ok) {
          setState("verified");
        } else {
          setState("forbidden");
        }
      })
      .catch(() => {
        if (!cancelled) setState("network-error");
      });

    return () => {
      cancelled = true;
    };
  }, [isLoaded, isSignedIn]);

  useEffect(() => {
    if (e2eBypass) return checkAdminAccess();
    if (!isLoaded) return;
    if (!isSignedIn) {
      setState("unauthenticated");
      return;
    }
    return checkAdminAccess();
  }, [e2eBypass, isLoaded, isSignedIn, checkAdminAccess]);

  if ((!e2eBypass && !isLoaded) || state === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-3 text-slate-500">
          <Shield className="h-8 w-8 animate-pulse text-teal-700" />
          <p className="text-sm">Verifying admin access…</p>
        </div>
      </div>
    );
  }

  if (state === "timed-out") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-sm text-center space-y-3">
          <Shield className="h-8 w-8 mx-auto text-amber-500" />
          <h2 className="text-lg font-semibold text-slate-900">Session timed out</h2>
          <p className="text-sm text-slate-500">
            You were signed out after 15 minutes of inactivity. Please sign in again to access the admin panel.
          </p>
          <a
            href={`${basePath}/sign-in`}
            className="inline-block mt-2 px-4 py-2 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800 transition-colors"
          >
            Sign in
          </a>
        </div>
      </div>
    );
  }

  if (state === "unauthenticated") {
    return <Redirect to="/sign-in" />;
  }

  if (state === "network-error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-sm text-center space-y-3 shadow-sm">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-amber-50 mx-auto">
            <WifiOff className="h-7 w-7 text-amber-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">Connection problem</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Could not reach the server to verify your admin access. Check your connection and try again.
          </p>
          <div className="pt-1 flex flex-col gap-2">
            <button
              onClick={() => checkAdminAccess()}
              className="inline-block px-4 py-2 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800 transition-colors cursor-pointer"
            >
              Retry
            </button>
            <a
              href={`${basePath}/`}
              className="inline-block px-4 py-2 text-slate-600 text-sm rounded-lg hover:bg-slate-100 transition-colors"
            >
              Back to RheumHub
            </a>
          </div>
        </div>
      </div>
    );
  }

  if (state === "forbidden") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="bg-white rounded-xl border border-slate-200 p-8 max-w-sm text-center space-y-3 shadow-sm">
          <div className="flex items-center justify-center w-14 h-14 rounded-full bg-red-50 mx-auto">
            <Lock className="h-7 w-7 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-slate-900">Access denied</h2>
          <p className="text-sm text-slate-500 leading-relaxed">
            Your account does not have admin privileges. If you believe this is an
            error, please contact a system administrator.
          </p>
          <div className="pt-1 flex flex-col gap-2">
            <a
              href={`${basePath}/`}
              className="inline-block px-4 py-2 bg-teal-700 text-white text-sm rounded-lg hover:bg-teal-800 transition-colors"
            >
              Back to RheumHub
            </a>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
